import React, { useContext, useEffect, useState } from 'react';
import Box from '@material-ui/core/Box';
import { IconButton, Paper, Typography } from '@material-ui/core';
import moment from 'moment';
import TextField from '@material-ui/core/TextField';
import HighlightOffOutlinedIcon from '@material-ui/icons/HighlightOffOutlined';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import WarningIcon from '@material-ui/icons/Warning';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import Tooltip from '@material-ui/core/Tooltip';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import { isEqual } from 'lodash';
import Modal from '@material-ui/core/Modal';
import {
  convertToMinutes,
  getNewDuration,
  recalculateDurations,
} from './availibilityFieldHelpers';
import { CalendarEditAvailabilityContext } from '../../Context';

const useStyles = makeStyles((theme) => ({
  flexContainer: {
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
  },
  availabilityFieldsContainer: {
    marginTop: '12px',
  },
  availabilityFieldsColumn: {
    textAlign: 'left',
    paddingLeft: '10px',
    paddingTop: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  timeInput: {
    backgroundColor: '#ffffff',
    marginLeft: '32px',
    '& .MuiOutlinedInput-adornedEnd': {
      display: 'none',
    },
    '& .MuiIconButton-root': {
      display: 'none',
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 6px',
    },
    '& input[type="time"]::-webkit-calendar-picker-indicator': {
      display: 'none',
      '-webkit-appearance': 'none',
    },
  },
  deleteButton: {
    padding: '4px',
    marginRight: '16px',
  },
  copyToAllContainer: {
    height: '60px',
  },
  copyModal: {
    position: 'absolute',
    width: 300,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
    borderRadius: '4px',
    padding: theme.spacing(2, 2, 3),
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
  copyModalButton: {
    marginRight: '12px',
  },
}));
const minutesInHour = 60;
const defaultDuration = 1;

const TimeBlock = ({
  time,
  idx,
  handleChange,
  handleBlur,
  deleteTimeBlock,
  numTimeBlocks,
}) => {
  const classes = useStyles();
  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      <Box
        flexGrow={1}
        display="flex"
        justifyContent="flex-start"
        alignItems="center"
        m={1}
      >
        <TextField
          id={`time--${idx}`}
          type="time"
          variant="outlined"
          value={moment(time.time, 'HH:mm:ss').format('HH:mm')}
          className={classes.timeInput}
          style={{ marginRight: '12px' }}
          onChange={(e) => handleChange(e)}
          onBlur={() => handleBlur()}
          error={time.error}
        />
        <small
          className="duration"
          id={`duration--${idx}`}
          style={{ color: time.error ? 'red' : 'gray' }}
        >
          {time.error ? (
            <Tooltip
              title="Time is outside of your start and end of day"
              aria-label="Time is outside of your start and end of day"
            >
              <WarningIcon />
            </Tooltip>
          ) : (
            `${time.duration} ${time.duration === 1 ? 'hr' : 'hrs'}`
          )}
        </small>
      </Box>
      {numTimeBlocks > 1 ? (
        <IconButton
          className={classes.deleteButton}
          id={`delete-time--${idx}`}
          onClick={(e) => deleteTimeBlock(e)}
        >
          <HighlightOffOutlinedIcon id={`delete-time-icon--${idx}`} />
        </IconButton>
      ) : (
        <div style={{ width: '32px', marginRight: '16px' }} />
      )}
    </Box>
  );
};

const AvailabilityFields = ({
  startTime,
  endTime,
  renderRulesTable,
  dayOfWeek = null,
  isServiceBay = false,
  timeBlocks,
  setTimeBlocks,
}) => {
  const {
    recurringDayDetails,
    setRecurringDayDetails,
    setHasSingleDayTimeBlockError,
    recurringDayErrors,
    setRecurringDayErrors,
    selectedRecurringDays,
    setCopiedTimeBlocks,
    hasWorkplaceService,
    allScheduleTypes,
    allClientLocations,
  } = useContext(CalendarEditAvailabilityContext);

  const daysOfWeek = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  const maxNumberOfTimeBlocksBeforeCaption = 15;

  const [openCopyModal, setOpenCopyModal] = useState(false);
  const classes = useStyles();

  const handleError = (timeBlocksMap) => {
    const hasError = timeBlocksMap.some((time) => time.error);

    if (dayOfWeek) {
      const dayOfWeekIndex = daysOfWeek.indexOf(dayOfWeek);
      const updatedDayErrors = [...recurringDayErrors];
      updatedDayErrors[dayOfWeekIndex] = hasError;
      setRecurringDayErrors(updatedDayErrors);
    } else setHasSingleDayTimeBlockError(hasError);
  };

  useEffect(() => {
    const timeBlocksMap = timeBlocks.map(({ time, duration }) => ({
      time,
      duration,
      error:
        convertToMinutes(time) < convertToMinutes(startTime) ||
        convertToMinutes(time) >= convertToMinutes(endTime),
    }));

    handleError(timeBlocksMap);
  }, [timeBlocks]);

  const validateTimeInput = (value) => {
    const valueInMinutes = convertToMinutes(value);
    const startTimeInMinutes = convertToMinutes(startTime);
    const endTimeInMinutes = convertToMinutes(endTime);

    return (
      valueInMinutes < startTimeInMinutes || valueInMinutes >= endTimeInMinutes
    );
  };

  const handleRecurringDayDetailsChange = (updatedTimes) => {
    const newRecurringDayDetails = [...recurringDayDetails];

    const recurringDay = newRecurringDayDetails.find(
      (day) => day.day_of_week === dayOfWeek,
    );

    const dayOfWeekIndex = daysOfWeek.indexOf(recurringDay?.dayOfWeek);

    const newTimes = updatedTimes.map((time) => time.time);
    const isChanged = !isEqual(
      recurringDay?.predetermined_slot_times,
      newTimes,
    );

    if (isChanged) {
      recurringDay.predetermined_slot_times = newTimes;
      recurringDay.is_changed = true;

      setRecurringDayDetails(newRecurringDayDetails);

      const updatedDayErrors = [...recurringDayErrors];
      updatedDayErrors[dayOfWeekIndex] = updatedTimes.some(
        (time) => time.error,
      );
      setRecurringDayErrors(updatedDayErrors);
    }
  };

  const addTimeBlock = () => {
    const times = [...timeBlocks];

    const lastEntry = times[times.length - 1];
    const lastTime = lastEntry.time;
    const lastTimeInt = convertToMinutes(lastTime);
    const minutesIncrement = defaultDuration * minutesInHour;

    const newTimeInt = lastTimeInt + minutesIncrement;

    const newHour = Math.floor(newTimeInt / minutesInHour);
    const newMinutes = newTimeInt % minutesInHour;
    const newTimeString = `${newHour}:${`${newMinutes}`.padEnd(2, '0')}:00`;
    const newTime = {
      time: newTimeString,
      duration: getNewDuration(newTimeString, endTime),
      error: validateTimeInput(newTimeString),
    };

    times.push(newTime);

    const updatedTimes = recalculateDurations(times, startTime, endTime);
    setTimeBlocks(updatedTimes);
    handleError(updatedTimes);

    if (dayOfWeek) handleRecurringDayDetailsChange(updatedTimes);
  };

  const deleteTimeBlock = (event) => {
    let { id } = event.target;

    // The ID will be a blank string if we click on the SVG path.
    // If so, we need to get the ID of the parent element
    if (id.length === 0) {
      id = event.target.parentElement.id;
    }

    const times = [...timeBlocks];

    // Because the SVG icon is it's own event target
    // we need to make sure we account for both the button, and the icon
    // This extracts the index from the element, then converts it to a number type using the unary plus (+) operator
    // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Unary_plus
    const elementIndex = +id.replace(
      id.includes('icon') ? 'delete-time-icon--' : 'delete-time--',
      '',
    );
    const filteredTimes = times.filter((time, idx) => idx !== elementIndex);
    const updatedTimes = recalculateDurations(
      filteredTimes,
      startTime,
      endTime,
    );

    const lastEntry = updatedTimes[updatedTimes.length - 1];
    lastEntry.duration = getNewDuration(lastEntry.time, endTime);

    setTimeBlocks(updatedTimes);
    handleError(updatedTimes);

    if (dayOfWeek) handleRecurringDayDetailsChange(updatedTimes);
  };

  const handleChange = (event) => {
    const { id } = event.target;
    const { value } = event.target;

    const times = [...timeBlocks];
    const elementIndex = +id.replace('time--', '');
    const current = times[elementIndex];
    const currentDuration = current.duration;

    const timeBlockExists = timeBlocks.find(
      (timeBlock) => timeBlock.time === `${value}:00`,
    );

    times[elementIndex] = {
      time: `${value}:00`,
      duration: currentDuration,
      error: validateTimeInput(value) || timeBlockExists,
    };

    const updatedTimes = [...times];

    setTimeBlocks(updatedTimes);
    handleError(updatedTimes);
  };

  const handleBlur = () => {
    if (timeBlocks.some((time) => time.error)) {
      handleError(timeBlocks);
      return;
    }

    const sortedTimes = recalculateDurations(
      timeBlocks.sort((a, b) => {
        const aInMinutes = convertToMinutes(a.time);
        const bInMinutes = convertToMinutes(b.time);

        return aInMinutes - bInMinutes;
      }),
      startTime,
      endTime,
    );

    const updatedTimes = [...sortedTimes];
    setTimeBlocks(updatedTimes);
    handleError(updatedTimes);

    if (dayOfWeek) handleRecurringDayDetailsChange(updatedTimes);
  };

  const handleClose = () => setOpenCopyModal(false);

  const handleCopy = () => {
    const times = [...timeBlocks];
    const recurringDay = recurringDayDetails.find(
      (day) => day.day_of_week === dayOfWeek,
    );

    const newRecurringDayDetails = [...recurringDayDetails];
    newRecurringDayDetails.map((day) => {
      if (day.is_canceled) return day;

      day.start_time = startTime;
      day.end_time = endTime;
      day.limit_scheduled_works = recurringDay.limit_scheduled_works;
      day.predetermined_slot_times = times.map((time) => time.time);

      if (!isServiceBay)
        day.limit_total_driving_hours = recurringDay.limit_total_driving_hours;
      if (allScheduleTypes.length > 1)
        day.offered_service_types = recurringDay.offered_service_types;
      if (hasWorkplaceService && allClientLocations.length > 0)
        day.client_location_ids = recurringDay.client_location_ids;

      day.is_changed = true;
    });
    setRecurringDayDetails(newRecurringDayDetails);
    setCopiedTimeBlocks(times);
    handleClose();
  };

  const daysToCopy = [...selectedRecurringDays];
  const availableDaysArray = daysToCopy
    .map((entry, index) => (entry ? daysOfWeek[index] : ''))
    .filter((day) => day && day !== dayOfWeek);
  const availableDaysText = availableDaysArray.join(', ');

  return (
    <>
      <Box
        className={`${classes.flexContainer} ${classes.availabilityFieldsContainer}`}
      >
        <Paper>
          <Grid container xs={12} style={{ padding: '8px 0' }}>
            <Grid item xs={6}>
              <div className={classes.availabilityFieldsColumn}>
                <strong>Start Times</strong>
                <Tooltip
                  title={`This is a list of all selectable time blocks for the day. When booking, customers will only be able to select the start times that you have set for the day. If a customers appointment takes longer than a single time block, the following blocks will also be booked. `}
                  aria-label="Edit time blocks"
                >
                  <InfoOutlinedIcon
                    style={{ marginLeft: '4px', color: 'rgb(180,180,180)' }}
                  />
                </Tooltip>
              </div>
              {timeBlocks.length >= maxNumberOfTimeBlocksBeforeCaption && (
                <Typography variant="caption" style={{ color: 'gray' }}>
                  Additional Start Times may require customers to scroll
                </Typography>
              )}
              {timeBlocks.map((time, idx) => (
                <TimeBlock
                  time={time}
                  idx={idx}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  deleteTimeBlock={deleteTimeBlock}
                  id={`timeblock-rule-${idx}`}
                  key={`timeblock-${idx}`}
                  numTimeBlocks={timeBlocks.length}
                />
              ))}
              <Box className={classes.flexContainer}>
                <Button
                  style={{ marginBottom: '12px' }}
                  onClick={() => addTimeBlock()}
                >
                  <AddCircleOutlineIcon style={{ marginRight: '4px' }} /> Add
                  Time
                </Button>
              </Box>
            </Grid>
            <Grid item xs={6} style={{ borderLeft: '1px solid #eee' }}>
              <div className={classes.availabilityFieldsColumn}>
                <strong>Rules</strong>

                <Tooltip
                  title="Set rules to limit and manage your day."
                  aria-label="Set rules to limit and manage your day."
                >
                  <InfoOutlinedIcon
                    style={{ marginLeft: '4px', color: 'rgb(180,180,180)' }}
                  />
                </Tooltip>
              </div>
              {renderRulesTable()}
            </Grid>
          </Grid>
        </Paper>
        {dayOfWeek && (
          <Box
            className={`${classes.flexContainer} ${classes.copyToAllContainer}`}
          >
            <Button
              color="primary"
              disabled={timeBlocks.some((time) => time.error)}
              onClick={() => {
                setOpenCopyModal(true);
              }}
            >
              <FileCopyOutlinedIcon /> Copy to All
            </Button>
          </Box>
        )}
      </Box>
      {dayOfWeek && (
        <Modal open={openCopyModal} onClose={handleClose}>
          <div className={classes.copyModal}>
            <h4>
              <b>{`Copy ${dayOfWeek}'s schedule to the following days?`}</b>
            </h4>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {availableDaysText}
            </ul>
            <Box display="flex" justifyContent="center">
              <Button
                className={classes.copyModalButton}
                variant="contained"
                color="primary"
                onClick={handleCopy}
              >
                Copy to selected days
              </Button>
              <Button
                className={classes.copyModalButton}
                variant="contained"
                onClick={handleClose}
              >
                Cancel
              </Button>
            </Box>
          </div>
        </Modal>
      )}
    </>
  );
};

export default AvailabilityFields;
